import React from "react";
import { evaluateWindowFormFieldVisibility } from "./windowFormDefinitionUtils";

/*
  Styling matches the Interior Door form (src/views/Forms/Door/InteriorDoor.js)
  so the window forms read as the same product: one card per section, small
  uppercase grey section heading, and the same control/label treatment.
*/
const CONTROL_CLASSES =
  "app-input w-full rounded-xl border px-3 py-2.5 text-sm transition-colors";

const LABEL_CLASSES = "mb-1 block text-sm font-medium";

const CARD_CLASSES = "app-card rounded-2xl border overflow-hidden";

const columnClassMap = {
  1: "grid-cols-1 md:grid-cols-1",
  2: "grid-cols-1 md:grid-cols-2",
  3: "grid-cols-1 md:grid-cols-3",
  4: "grid-cols-1 md:grid-cols-4",
  5: "grid-cols-1 md:grid-cols-5",
  6: "grid-cols-1 md:grid-cols-6",
  7: "grid-cols-1 md:grid-cols-7",
  8: "grid-cols-1 md:grid-cols-8",
  9: "grid-cols-1 md:grid-cols-9",
  10: "grid-cols-1 md:grid-cols-10",
};

const getGridColumnsClass = (columns) =>
  columnClassMap[columns] || columnClassMap[2];

const normalizeDateValue = (value) => {
  if (!value) {
    return "";
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().slice(0, 10);
  }
  if (typeof value === "string") {
    return value.split("T")[0];
  }
  return "";
};

const normalizeNumberValue = (value) => {
  if (value === 0) {
    return "0";
  }
  if (value === null || value === undefined) {
    return "";
  }
  return String(value);
};

const FieldLabel = ({ field }) => (
  <label className={LABEL_CLASSES}>
    {field.label || "Field"}
    {field.required && (
      <span className="ml-0.5 text-rose-500 dark:text-rose-400">*</span>
    )}
  </label>
);

/*
  One renderer for every field type. The repeatable and flat branches below
  differ only in where the value comes from and where the change goes, so they
  share this — the markup used to be duplicated for both.
*/
const FieldControl = ({ field, value, onChange, radioName }) => {
  if (field.type === "spacer") {
    return <div className="h-6 md:h-8" />;
  }

  if (field.type === "dropdown") {
    return (
      <div>
        <FieldLabel field={field} />
        <select
          className={CONTROL_CLASSES}
          value={value}
          required={field.required}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Select...</option>
          {(field.options || []).map((option, optionIndex) => (
            <option key={`${option}-${optionIndex}`} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === "date") {
    return (
      <div>
        <FieldLabel field={field} />
        <input
          type="date"
          className={CONTROL_CLASSES}
          value={normalizeDateValue(value)}
          required={field.required}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    );
  }

  if (field.type === "quantity") {
    return (
      <div>
        <FieldLabel field={field} />
        <input
          type="number"
          min="0"
          step="1"
          inputMode="numeric"
          className={CONTROL_CLASSES}
          value={normalizeNumberValue(value)}
          required={field.required}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    );
  }

  if (field.type === "radio") {
    return (
      <div>
        <FieldLabel field={field} />
        <div className="space-y-2">
          {(field.options || []).map((option, optionIndex) => (
            <label
              key={`${field.id}-${optionIndex}`}
              className="flex min-h-[2rem] cursor-pointer items-center gap-2 text-sm"
            >
              <input
                type="radio"
                name={radioName}
                value={option}
                checked={value === option}
                required={field.required}
                onChange={(event) => onChange(event.target.value)}
              />
              {option}
            </label>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <FieldLabel field={field} />
      <input
        className={CONTROL_CLASSES}
        value={value}
        required={field.required}
        onChange={(event) => onChange(event.target.value)}
      />
    </div>
  );
};

const CustomFormSections = ({
  sections = [],
  values = {},
  onFieldChange,
  onSectionChange,
}) => {
  if (!sections.length) {
    return null;
  }

  return (
    <div className="space-y-5">
      {sections.map((section, sectionIndex) => {
        const fields = section.fields || [];
        const isRepeatable = section.type === "repeatable";
        const gridColumnsClass = getGridColumnsClass(section.columns || 2);
        const rows = Array.isArray(values?.[section.id])
          ? values[section.id]
          : [{}];
        const updateRows = (nextRows) => {
          onSectionChange?.(section.id, nextRows);
        };

        return (
          <div key={section.id || sectionIndex} className={CARD_CLASSES}>
            <div className="px-5 py-5 sm:px-6">
              <h2 className="app-eyebrow mb-3">
                {section.title || "Custom Section"}
              </h2>

              {fields.length === 0 ? (
                <p className="app-muted text-sm">
                  No fields configured for this section.
                </p>
              ) : isRepeatable ? (
                <div className="space-y-4">
                  {rows.map((row, rowIndex) => (
                    <div
                      key={`${section.id}-row-${rowIndex}`}
                      className="app-surface rounded-xl border p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <span className="app-eyebrow">
                          Row {rowIndex + 1}
                        </span>
                        <button
                          type="button"
                          className="app-btn app-btn-ghost app-btn-sm text-rose-600 dark:text-rose-300"
                          onClick={() => {
                            if (rows.length === 1) {
                              return;
                            }
                            updateRows(
                              rows.filter((_, index) => index !== rowIndex)
                            );
                          }}
                          disabled={rows.length === 1}
                        >
                          Remove
                        </button>
                      </div>

                      <div className={`grid gap-4 ${gridColumnsClass}`}>
                        {fields
                          .filter((field) =>
                            evaluateWindowFormFieldVisibility(
                              field,
                              values,
                              row
                            )
                          )
                          .map((field, fieldIndex) => (
                            <FieldControl
                              key={field.id || fieldIndex}
                              field={field}
                              value={row?.[field.id] ?? ""}
                              radioName={`${section.id}-${field.id}-${rowIndex}`}
                              onChange={(nextValue) =>
                                updateRows(
                                  rows.map((item, index) =>
                                    index === rowIndex
                                      ? { ...item, [field.id]: nextValue }
                                      : item
                                  )
                                )
                              }
                            />
                          ))}
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    className="app-btn app-btn-secondary"
                    onClick={() => updateRows([...rows, {}])}
                  >
                    <span className="text-base leading-none">+</span>
                    Add Row
                  </button>
                </div>
              ) : (
                <div className={`grid gap-4 ${gridColumnsClass}`}>
                  {fields
                    .filter((field) =>
                      evaluateWindowFormFieldVisibility(field, values)
                    )
                    .map((field, fieldIndex) => (
                      <FieldControl
                        key={field.id || fieldIndex}
                        field={field}
                        value={values?.[field.id] ?? ""}
                        radioName={`${section.id}-${field.id}`}
                        onChange={(nextValue) =>
                          onFieldChange?.(field.id, nextValue)
                        }
                      />
                    ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CustomFormSections;
